// Access-change history (Platform Operations Console P2). A focused, cross-tenant
// governance feed of privilege/security changes — role assignments, operator
// lifecycle, tenant lifecycle, impersonation, high-risk access — read from the
// immutable audit log (auth-service /audit-logs, curated to the access actions).
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag, Select, Button, Space, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader, useUrlTableState, SavedViews, ExportButton, type CsvColumn } from '../../components/console';
import {
  getAuditLogs, ACCESS_ACTIONS, ACCESS_ACTIONS_CSV, type AuditLog,
} from '../../services/auditService';

const { Text } = Typography;

const LABEL: Record<string, string> = Object.fromEntries(ACCESS_ACTIONS.map((a) => [a.value, a.label]));

// Semantic colour by what the action implies (Ant Tag colour → KaziPeople tokens).
const actionColor = (action: string): string => {
  if (action === 'tenant.terminate' || action === 'high_risk_access_attempt' || action === 'operator.deactivate') return 'error';
  if (action === 'tenant.suspend' || action === 'impersonate' || action === 'session.revoke') return 'warning';
  if (action === 'operator.activate' || action === 'tenant.reactivate') return 'success';
  return 'blue'; // role assignments / invites
};

const fmt = (s?: string) => (s ? fmtDateTime(s) : '—');

// Human one-liner describing what changed, from the audit metadata.
const detail = (log: AuditLog): React.ReactNode => {
  const m = (log.metadata ?? {}) as Record<string, any>;
  if (log.action === 'user.role.assign') {
    const oldR = Array.isArray(m.oldRoles) ? m.oldRoles.join(', ') : '—';
    const newR = Array.isArray(m.newRoles) ? m.newRoles.join(', ') : '—';
    return <Text type="secondary">{oldR} → <Text strong>{newR}</Text></Text>;
  }
  if (m.reason) return <Text type="secondary">“{String(m.reason)}”</Text>;
  return <Text type="secondary">—</Text>;
};

const EXPORT_COLUMNS: CsvColumn<AuditLog>[] = [
  { header: 'Action', value: (r) => LABEL[r.action] || r.action },
  { header: 'Actor', value: (r) => r.user?.email || 'system' },
  { header: 'Target', value: (r) => { const m = (r.metadata ?? {}) as Record<string, any>; return m.targetEmail || r.entityName || m.actorEmail || ''; } },
  {
    header: 'Detail',
    value: (r) => {
      const m = (r.metadata ?? {}) as Record<string, any>;
      if (r.action === 'user.role.assign') return `${(m.oldRoles || []).join('|')} -> ${(m.newRoles || []).join('|')}`;
      return m.reason || '';
    },
  },
  { header: 'Tenant', value: (r) => r.tenant?.name || 'platform' },
  { header: 'When', value: (r) => (r.timestamp ? new Date(r.timestamp).toISOString() : '') },
];

export const AccessChangesPage: React.FC = () => {
  const url = useUrlTableState(['action']);
  const [rows, setRows] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const pageSize = 50;

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await getAuditLogs({
        page: url.page,
        limit: pageSize,
        // A single selected action, else the whole curated access set.
        actions: url.filters.action ? undefined : ACCESS_ACTIONS_CSV,
        action: url.filters.action || undefined,
      });
      setRows(res.logs || []);
      setTotal(res.pagination?.total ?? 0);
    } catch {
      if (initial) message.error('Could not load access changes');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [url.page, url.filters.action]);

  useEffect(() => { void load(true); }, [load]);

  const columns: ColumnsType<AuditLog> = useMemo(() => [
    {
      title: 'Action', dataIndex: 'action', width: 190,
      render: (a: string) => <Tag color={actionColor(a)}>{LABEL[a] || a}</Tag>,
    },
    {
      title: 'Actor', width: 220,
      render: (_: unknown, r) => r.user?.email ? <Text>{r.user.email}</Text> : <Text type="secondary">system</Text>,
    },
    {
      title: 'Target',
      render: (_: unknown, r) => {
        const m = (r.metadata ?? {}) as Record<string, any>;
        const t = m.targetEmail || r.entityName || m.actorEmail || '—';
        return <Text>{String(t)}</Text>;
      },
    },
    { title: 'Detail', render: (_: unknown, r) => detail(r) },
    { title: 'Tenant', width: 160, render: (_: unknown, r) => r.tenant?.name || <Text type="secondary">platform</Text> },
    { title: 'When', dataIndex: 'timestamp', width: 180, render: (t: string) => fmt(t) },
  ], []);

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Access changes"
        subtitle="Privilege & security changes across all tenants — from the immutable audit log"
        actions={
          <Space wrap>
            <Select
              allowClear
              placeholder="All access actions"
              style={{ width: 220 }}
              value={url.filters.action || undefined}
              onChange={(v) => url.setFilter('action', v)}
              options={ACCESS_ACTIONS.map((a) => ({ value: a.value, label: a.label }))}
            />
            <SavedViews pageKey="access-changes" />
            <ExportButton filename="access-changes" rows={rows} columns={EXPORT_COLUMNS} />
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => void load(false)} disabled={refreshing}>
              Refresh
            </Button>
          </Space>
        }
      />

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{
          current: url.page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (p) => url.setPage(p),
        }}
      />
    </div>
  );
};

export default AccessChangesPage;
